import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Modal, DesignedModal, Button } from '@clickcease/cc-client-components-lib'
import { useTranslation } from 'react-i18next'
import { AddDomainInner } from 'Components/Elements/AddDomainModal/SingleDomain/AddDomainInner'
import { AddDomainWrapper } from './SingleDomain/AddDomainModal.styled'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { MultipleDomains } from './MultipleDomains/MultipleDomains'
import { useSplit } from 'Hooks/useSplit'
import { NewMultipleDomains } from './NewMultipleDomains/NewMultipleDomains'
import { useAddDomain } from 'Features/domainsManagement/api/addDomain'
import useAddDomainStore from './NewMultipleDomains/store/addDomainsStore'
import {
  addDomainModalContentStyle,
  addDomainModalStyle,
} from './NewMultipleDomains/NewMultipleDomains.styles'
import { ConfirmationModal } from './NewMultipleDomains/components/ConfirmationModal'
import { MAX_DOMAINS } from './NewMultipleDomains/helpers'

interface AddDomainModalProps {
  isOpen?: boolean
  onOpenChange: (a: boolean | number) => void
}

const Description = () => {
  const { t } = useTranslation('domainManagement')
  return (
    <div>
      <div>{t('add_new_domain_modal.description_single_domain')}</div>
    </div>
  )
}

export const AddDomainModal = ({ onOpenChange }: AddDomainModalProps) => {
  const { t } = useTranslation('domainManagement')
  const location = useLocation()
  const navigate = useNavigate()
  const { selectedDomains, allDomainsInTable } = useAddDomainStore()
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)

  const { mutate, isLoading } = useAddDomain()

  const { data } = useGetPlansDetails()

  const domainsNewUiSplit = useSplit('CE_NEW_MULTI_DOMAIN')

  const doseAllDomainsSelected = allDomainsInTable.length === selectedDomains.length

  // Redirect to domain management if user is on paid marketing dashboard
  // Because in some cases the user can't see the modal (welcoming experience)
  useEffect(
    () => {
      if (location.pathname === '/paid-marketing/dashboard') navigate('/domain-management')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  )

  const isAdvanced = data && (data.plans || []).some((plan) => plan.type === 'Advanced')

  const domainsWithProducts = selectedDomains.filter(
    ({ botZapping, paidMarketing }) => botZapping || paidMarketing
  )

  const disabledAddDomainsButton =
    !domainsWithProducts.length || domainsWithProducts.length > MAX_DOMAINS

  const handleAddMultipleDomains = () => {
    if (domainsWithProducts.length) {
      setConfirmationModalOpen(false)
      mutate(
        { domains: domainsWithProducts },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    }
  }

  return domainsNewUiSplit ? (
    <>
      <DesignedModal
        isOpen
        title={t('add_new_multiple_domains_modal.title')}
        id="add-domain-modal"
        onOpenChange={onOpenChange}
        style={addDomainModalStyle}
        contentStyle={addDomainModalContentStyle}
        actions={
          <>
            <Button
              variant="outline"
              text={t('add_new_multiple_domains_modal.btn_close')}
              onClick={() => onOpenChange(false)}
            />
            <Button
              loading={isLoading}
              disabled={disabledAddDomainsButton}
              text={t('add_new_multiple_domains_modal.btn_add_selected')}
              onClick={
                doseAllDomainsSelected
                  ? handleAddMultipleDomains
                  : () => setConfirmationModalOpen(true)
              }
            />
          </>
        }
      >
        <NewMultipleDomains />
      </DesignedModal>

      {confirmationModalOpen && (
        <ConfirmationModal
          setIsOpen={setConfirmationModalOpen}
          onApprove={handleAddMultipleDomains}
        />
      )}
    </>
  ) : (
    <Modal
      isOpen
      title={t('add_new_domain_modal.title')}
      onOpenChange={onOpenChange}
      onCloseClick={() => {}}
      id={'add-domain-modal'}
    >
      <AddDomainWrapper isMultiDomain={isAdvanced}>
        {isAdvanced ? (
          <MultipleDomains onOpenChange={onOpenChange} />
        ) : (
          <AddDomainInner onOpenChange={onOpenChange} description={<Description />} />
        )}
      </AddDomainWrapper>
    </Modal>
  )
}
